import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Link2, Eraser, Image as ImageIcon } from "lucide-react";

// Editor WYSIWYG leve (sem dependência): contentEditable + execCommand.
// Gera HTML, sanitizado no carregamento e renderizado com DOMPurify na leitura.
// O conteúdo é montado num fundo branco (estilo inline) p/ ficar legível no dark.
const RichTextEditor = ({ value = "", onChange, placeholder = "Escreva o artigo..." }) => {
  const ref = useRef(null);
  const imgRef = useRef(null);

  // Seta o HTML inicial só uma vez (evita pular o cursor a cada digitação).
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = DOMPurify.sanitize(value || "");
    }
    // Monta o HTML inicial apenas uma vez (deps vazias de proposito).
  }, []);

  const emit = () => onChange?.(ref.current?.innerHTML || "");

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  };

  const addLink = () => {
    const url = window.prompt("URL do link:", "https://");
    if (url) exec("createLink", url);
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert("Imagem muito grande (máx. 2 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      ref.current?.focus();
      document.execCommand("insertImage", false, reader.result);
      emit();
    };
    reader.readAsDataURL(file);
  };

  const Btn = ({ onClick, title, children }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="inline-flex h-8 w-8 items-center justify-center rounded text-gray-600 hover:bg-gray-200"
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-md border border-gray-300 overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1">
        <Btn onClick={() => exec("bold")} title="Negrito"><Bold size={16} /></Btn>
        <Btn onClick={() => exec("italic")} title="Itálico"><Italic size={16} /></Btn>
        <Btn onClick={() => exec("underline")} title="Sublinhado"><Underline size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-gray-300" />
        <Btn onClick={() => exec("formatBlock", "h2")} title="Título"><Heading2 size={16} /></Btn>
        <Btn onClick={() => exec("insertUnorderedList")} title="Lista"><List size={16} /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Lista numerada"><ListOrdered size={16} /></Btn>
        <Btn onClick={addLink} title="Inserir link"><Link2 size={16} /></Btn>
        <Btn onClick={() => imgRef.current?.click()} title="Inserir imagem"><ImageIcon size={16} /></Btn>
        <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
        <span className="mx-1 h-5 w-px bg-gray-300" />
        <Btn onClick={() => exec("removeFormat")} title="Limpar formatação"><Eraser size={16} /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        data-placeholder={placeholder}
        className="kb-editor min-h-[240px] max-h-[440px] overflow-y-auto px-3 py-2 text-sm focus:outline-none"
        style={{ background: "#ffffff", color: "#0f172a" }}
      />
    </div>
  );
};

export default RichTextEditor;
